<template>
  <div class="entries-table-container" :class="outerContainerClasses">
    <div :class="innerContainerClasses">
      <div class="tw-grid tw-grid-cols-2 tw-items-center tw-mb-4">
        <div
          v-if="title"
          class="tw-text-sf-neutral-dark tw-font-medium tw-text-lg"
        >
          {{ title }}
        </div>
        <div class="tw-col-end-3">
          <SfSearchInput
            v-model="searchQuery"
            wide
            class="tw-flex-grow"
            show-search-icon
            :placeholder="I18n.t('detailed_entry.type_to_search')"
            :show-result-navigation="false"
            :show-clear-icon="!!searchQuery"
            @update:model-value="updateSearchQuery"
            @clear="clearSearch"
          />
        </div>
      </div>

      <SfTable
        v-if="props.list.length"
        class="entries-table-scroll-container"
        :auto-header-cell-width="true"
        :handles-sorting="false"
        :headers="headers"
        :sticky-header="stickyHeader"
        :initial-sort-by="sortBy"
        :initial-sort-direction="'desc'"
        :rows="sfTableRows"
        :non-sortable-headers="nonSortableHeaders"
        data-testid="entries-table"
        @table-sort="handleTableSort"
      >
        <template #header="{ header, headerIndex, maxHeaderCellWidth }">
          <SfTableHeaderCell
            :align="
              header === 'journal' || header === 'value' ? 'right' : undefined
            "
            :header-id="header.id || header"
            :header-index="headerIndex"
            :max-header-cell-width="maxHeaderCellWidth"
            :sortable="header !== 'document_reference' && header !== 'journal'"
            :sort-direction="sortDirection"
            :sort-by="sortBy"
          >
            <span
              v-if="header !== 'document_reference'"
              v-tooltip="props.translatedHeaders[headerIndex]"
            >
              {{ props.translatedHeaders[headerIndex] }}
            </span>
          </SfTableHeaderCell>
        </template>
        <template #rows="{ rows }">
          <EntriesTableRow
            v-for="row in rows"
            :key="(row.id as string | number)"
            :entry="row"
            :headers="headers"
            :hovered-attributes="hoveredAttributes"
            :sidebar-preview="sidebarPreview"
            @enter-row="handleRowHover"
            @leave-row="handleRowLeave"
          />
        </template>
      </SfTable>
      <caption
        v-else-if="!emptyBannerText"
        class="empty tw-block tw-text-center tw-text-lg tw-text-sf-gray"
      >
        {{
          I18n.t('common.empty')
        }}
      </caption>
      <div v-else-if="!searchQuery" class="tw-mt-4">
        <SfBannerInline
          variant="neutral"
          is-full-width
          :align="FlexAlign.CENTER"
          :text-align="FlexAlign.CENTER"
          :show-icon="false"
          :title="emptyBannerText"
        />
      </div>

      <div
        v-if="!!searchQuery && sfTableRows.length === 0"
        class="tw-flex tw-justify-center tw-mt-4"
      >
        <SfBannerInline
          variant="neutral"
          is-full-width
          :align="FlexAlign.CENTER"
          :text-align="FlexAlign.CENTER"
          :show-icon="false"
          :title="I18n.t('detailed_entry.no_results_found')"
        />
      </div>
    </div>
    <div v-if="truncated" class="tw-flex tw-flex-wrap tw-justify-center">
      {{ truncated }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TableEntry } from '@app/types'

import { v4 as generateUuid } from 'uuid'
import { computed, ref } from 'vue'

import { compareValues } from '@app/application/report/utils/entries-sort'
import EntriesTableRow from './entries-table-row.vue'
import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import SfTableHeaderCell from '@app/shared/vue/components/sf-table/sf-table-header-cell.vue'
import type { TableSortDirections } from '@app/shared/vue/components/sf-table/constants'
import { tableSortDirections } from '@app/shared/vue/components/sf-table/constants'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'
import { FlexAlign } from '@app/shared/constants'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'

interface Props {
  attributes?: string[]
  hasMinWidth?: boolean
  headers?: string[]
  isHoverable?: boolean
  list?: TableEntry[]
  title?: string
  translatedHeaders?: string[]
  truncated?: string
  sidebarPreview?: boolean
  stickyHeader?: boolean
  emptyBannerText?: string
}

const props = withDefaults(defineProps<Props>(), {
  attributes: () => [],
  headers: () => [],
  list: () => [],
  title: '',
  translatedHeaders: () => [],
  truncated: '',
  emptyBannerText: '',
})

const I18n = window.I18n

const nonSortableHeaders = ['document_reference', 'journal']

const hoveredAttributes = ref<string[]>([])

const sortBy = ref(
  props.headers[0] === 'journal' ? props.headers[1] : props.headers[0]
)
const sortDirection = ref<TableSortDirections>(tableSortDirections.DESC)

const outerContainerClasses = computed(() => ({
  'entries-table-container--min-width': !!props.hasMinWidth,
}))

const innerContainerClasses = computed(() => ({
  'tw-mb-2': !!props.truncated,
}))

const searchQuery = ref('')
const clearSearch = () => {
  searchQuery.value = ''
}
const updateSearchQuery = (value: string | null) => {
  searchQuery.value = value ? value : ''
}

const effectiveRows = computed(() => {
  const searchTerm = searchQuery.value.toLowerCase().replace(/[.,]/g, '')

  return props.list
    .filter(
      (entry) =>
        searchQuery.value === '' ||
        props.headers.some(
          (header) =>
            entry[header] &&
            entry[header]
              .toString()
              .toLowerCase()
              .replace(/[.,]/g, '')
              .includes(searchTerm)
        )
    )
    .map((entry) => ({
      ...entry,

      attributes: props.attributes.reduce((acc, attribute) => {
        if (entry[attribute]) {
          acc[attribute] = entry[attribute]
        }

        return acc
      }, {} as Record<string, unknown>),
    }))
})

const sortedList = computed(() => {
  const listCopy = [...effectiveRows.value]
  return listCopy.sort(sortFunction)
})

const sfTableRows = computed<TableEntry[]>(() => {
  return sortedList.value.map((entry) => {
    const rawRow = props.headers.reduce((acc, header) => {
      acc[header] = (entry as Record<string, unknown>)[header]
      return acc
    }, {} as TableEntry)

    if (props.isHoverable) {
      rawRow.attributes = entry.attributes
    }

    rawRow.journal_link = entry.journal_link
    rawRow.link_to_adjustment = entry.link_to_adjustment
    rawRow.document_properties = entry.document_properties
    rawRow.id = generateUuid()

    return rawRow
  })
})

function sortFunction(a: TableEntry, b: TableEntry): number {
  const rawKey = `${sortBy.value}_raw`
  const targetA =
    a[rawKey] !== null && a[rawKey] !== undefined ? a[rawKey] : a[sortBy.value]
  const targetB =
    b[rawKey] !== null && b[rawKey] !== undefined ? b[rawKey] : b[sortBy.value]
  const direction = sortDirection.value === tableSortDirections.DESC ? -1 : 1

  return (
    compareValues(targetA as string | number, targetB as string | number) *
    direction
  )
}

function handleTableSort({
  sortBy: tsortBy,
  sortDirection: tsortDirection,
}: {
  sortBy: string
  sortDirection: TableSortDirections
}) {
  if (tsortBy === 'document_reference' || tsortBy === 'journal') {
    return
  }

  sortBy.value = tsortBy
  sortDirection.value = tsortDirection
}

function handleRowHover(attributes: string[]) {
  if (!props.isHoverable) {
    return
  }

  hoveredAttributes.value = attributes
}

function handleRowLeave() {
  if (!props.isHoverable) {
    return
  }

  hoveredAttributes.value = []
}
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.entries-table-container {
  overflow-x: auto;

  &--min-width {
    min-width: 400px;

    @include medium {
      min-width: 900px;
    }

    @include large {
      min-width: 1200px;
    }
  }
}
</style>
